import React, { useRef, useState, useEffect } from 'react';
import { Button, Container, Message, Segment, Icon } from 'semantic-ui-react';
import ReactMarkdown from 'react-markdown';
import { useWindowSize } from 'usehooks-ts';
import ChatInput from './ChatInput';

// At the top of the file, add these constants
const LOGO = document.querySelector('meta[name="square-logo-path"]')?.content;

// Custom link renderer for ReactMarkdown
const CustomLink = ({ href, children }) => {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const formatTime = (date) => {
  const now = new Date();
  // If no date provided, return current time
  if (!date) {
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  const messageDate = new Date(date);
  const hours = messageDate.getHours().toString().padStart(2, '0');
  const minutes = messageDate.getMinutes().toString().padStart(2, '0');
  
  // If message is from a different day
  const timeDiff = now - messageDate;
  if (timeDiff > 24 * 60 * 60 * 1000) {
    const day = messageDate.getDate().toString().padStart(2, '0');
    const month = (messageDate.getMonth() + 1).toString().padStart(2, '0');
    const year = messageDate.getFullYear();
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  
  return `${hours}:${minutes}`;
};

const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const ThinkingMessage = ({ current_locale }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    preloadImage(LOGO);
  }, []);

  const translations = {
    en: "Thinking...",
    ar: "...جاري التفكير",
    tr: "Düşünüyor...",
    de: "Denken...",
    es: "Pensando...",
    fr: "Réflexion..."
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.25rem 1rem',
      color: '#666',
      fontSize: '0.9rem',
      direction: current_locale === 'ar' ? 'rtl' : 'ltr',
      opacity: imageLoaded ? 1 : 0.5, // Fade in when image is loaded
      transition: 'opacity 0.2s ease-in-out'
    }}>
      <img 
        src={LOGO}
        alt={current_locale === 'ar' ? "مساعد DCACLab" : "DCACLab Assistant"}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          visibility: imageLoaded ? 'visible' : 'hidden'
        }}
        onLoad={() => setImageLoaded(true)}
      />
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: '#f8f9fa',
        padding: '0.3rem 0.8rem',
        borderRadius: '1rem',
        border: '1px solid #e9ecef',
        direction: current_locale === 'ar' ? 'rtl' : 'ltr',
        textAlign: current_locale === 'ar' ? 'right' : 'left'
      }}>
        <Icon name="circle notched" loading />
        {translations[current_locale] || translations['en']}
      </div>
    </div>
  );
};

const ChatMessage = ({ message, isLoading, current_locale, messages }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!message.role === 'user') {
      preloadImage(LOGO);
    }
  }, [message.role]);

  const isUser = message.role === 'user';
  const timestamp = formatTime(message.created_at || new Date());
  const isRTL = current_locale === 'ar';
  const isIntro = !isUser && messages[0] === message && messages.length === 1;
  
  const translations = {
    en: {
      you: "You",
      assistant: "DCACLab Assistant",
      welcome: "Welcome to DCACLab AI Assistant! 👋",
      intro: "I'm here to help you with:",
      capabilities: [
        "Digital circuit design and analysis",
        "Circuit troubleshooting and optimization",
        "Lab safety and best practices",
        "Technical concepts and explanations"
      ],
      prompt: "Feel free to ask any questions about your circuit or digital electronics in general!"
    },
    ar: {
      you: "أنت",
      assistant: "DCACLab مساعد",
      welcome: "👋 !مرحباً بك في مساعد DCACLab الذكي",
      intro: "يمكنني مساعدتك في:",
      capabilities: [
        "تصميم وتحليل الدوائر الرقمية",
        "استكشاف الأخطاء وتحسين الدوائر",
        "إرشادات السلامة المختبرية",
        "المفاهيم التقنية والشروحات"
      ],
      prompt: "!لا تتردد في طرح أي سؤال حول الدوائر أو الإلكترونيات الرقمية"
    },
    tr: {
      you: "Sen",
      assistant: "DCACLab Asistanı",
      welcome: "DCACLab AI Asistanına Hoş Geldiniz! 👋",
      intro: "Size şu konularda yardımcı olabilirim:",
      capabilities: [
        "Dijital devre tasarımı ve analizi",
        "Devre sorunlarını giderme ve optimizasyonu",
        "Laboratuvar güvenliği ve iyi uygulamalar",
        "Teknik kavramlar ve açıklamalar"
      ],
      prompt: "Dijital elektronik veya devreler hakkında herhangi bir sorunuz varsa lütfen sorunuz!"
    },
    de: {
      you: "Sie",
      assistant: "DCACLab Assistent",
      welcome: "Willkommen beim DCACLab AI-Assistenten! 👋",
      intro: "Ich kann Ihnen helfen bei:",
      capabilities: [
        "Digitale Schaltkreisdesign und -analyse",
        "Schaltkreisfehlerbehebung und -optimierung",
        "Laboratoriumssicherheit und Best Practices",
        "Technische Konzepte und Erklärungen"
      ],
      prompt: "Stellen Sie gerne Fragen zu Ihrem Schaltkreis oder digitalen Elektronik allgemein!"
    },
    es: {
      you: "Tú",
      assistant: "Asistente DCACLab",
      welcome: "¡Bienvenido al Asistente de DCACLab AI! 👋",
      intro: "Puedo ayudarte con:",
      capabilities: [
        "Diseño y análisis de circuitos digitales",
        "Solución de problemas de circuitos y optimización",
        "Seguridad de laboratorio y mejores prácticas",
        "Conceptos técnicos y explicaciones"
      ],
      prompt: "¡No dudes en preguntar cualquier cosa sobre tus circuitos o electrónica digital en general!"
    },
    fr: {
      you: "Vous",
      assistant: "Assistant DCACLab",
      welcome: "Bienvenue dans l'Assistant DCACLab AI ! 👋",
      intro: "Je peux vous aider avec :",
      capabilities: [
        "Conception et analyse de circuits numériques",
        "Dépannage et optimisation des circuits",
        "Sécurité de laboratoire et meilleures pratiques",
        "Concepts techniques et explications"
      ],
      prompt: "N'hésitez pas à poser des questions sur vos circuits ou électronique numérique en général !"
    }
  };

  const currentTranslations = translations[current_locale] || translations['en'];
  
  if (isIntro) {
    return (
      <Message
        style={{
          margin: '1rem',
          borderRadius: '1rem',
          boxShadow: '0 4px 6px rgba(0, 122, 255, 0.1)',
          border: '1px solid rgba(0, 122, 255, 0.2)',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          opacity: imageLoaded ? 1 : 0.5,
          transition: 'opacity 0.2s ease-in-out'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1rem',
          opacity: imageLoaded ? 1 : 0.5,
          transition: 'opacity 0.2s ease-in-out'
        }}>
          <img 
            src={LOGO}
            alt="DCACLab Assistant"
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '12px',
              border: '2px solid rgba(0, 122, 255, 0.2)',
              visibility: imageLoaded ? 'visible' : 'hidden'
            }}
            onLoad={() => setImageLoaded(true)}
          />
          <div>
            <div style={{ 
              fontSize: '1.4em',
              color: '#007AFF',
              marginBottom: '0.3rem',
              fontWeight: '500'
            }}>
              {currentTranslations.welcome}
            </div>
            <div style={{ color: '#666' }}>
              {currentTranslations.intro}
            </div>
          </div>
        </div>
        
        <div style={{ marginTop: '1rem' }}>
          {currentTranslations.capabilities.map((capability, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <Icon name='check circle' color='blue' />
              <span>{capability}</span>
            </div>
          ))}
        </div>
        
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: 'rgba(0, 122, 255, 0.05)',
          borderRadius: '0.5rem',
          color: '#007AFF',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          justifyContent: 'center'
        }}>
          <Icon name='comment' />
          {currentTranslations.prompt}
        </div>
      </Message>
    );
  }
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: isUser ? 'flex-end' : 'flex-start',
      width: '100%',
      gap: '0.25rem',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.9rem',
        marginLeft: isRTL ? (isUser ? '2.5rem' : '0') : (isUser ? 'auto' : '2.5rem'),
        marginRight: isRTL ? (isUser ? 'auto' : '2.5rem') : 0,
        marginBottom: '0.1rem',
        justifyContent: isRTL ? (isUser ? 'flex-start' : 'flex-end') : (isUser ? 'flex-end' : 'flex-start')
      }}>
        {!isUser && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: '32px',
            marginLeft: isRTL ? 0 : '-3rem',
            marginRight: isRTL ? '-3rem' : 0
          }}>
            <img 
              src={LOGO}
              alt={isRTL ? "مساعد DCACLab" : "DCACLab Assistant"}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                visibility: imageLoaded ? 'visible' : 'hidden'
              }}
              onLoad={() => setImageLoaded(true)}
            />
          </div>
        )}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          flexDirection: isRTL ? 'row' : 'row'
        }}>
          <span style={{ 
            fontSize: '0.8rem',
            order: isRTL ? 1 : 0,
            whiteSpace: 'nowrap'
          }}>{isUser ? currentTranslations.you : currentTranslations.assistant}</span>
          <span style={{ 
            fontSize: '0.8rem', 
            color: '#999',
            order: isRTL ? 0 : 1,
            marginLeft: isRTL ? 0 : '0.5rem',
            marginRight: isRTL ? '0.5rem' : 0,
            whiteSpace: 'nowrap'
          }}>
            {timestamp}
          </span>
        </div>
      </div>
      
      <Message
        style={{
          marginLeft: isRTL ? (isUser ? 0 : 'auto') : (isUser ? 'auto' : 0),
          marginRight: isRTL ? (isUser ? 'auto' : 0) : (isUser ? 0 : 'auto'),
          maxWidth: '85%',
          backgroundColor: isUser ? '#C6F4D6' : '#F0F7FF',  
          color: isUser ? '#1B5E20' : '#0A4B8C',  
          borderRadius: '1rem',
          padding: '0.75rem 1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          border: 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          margin: '0 !important',
          marginTop: '0 !important'
        }}
        className="chat-message no-margin-message"
      >
        <Message.Content style={{ 
          padding: 0,
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}>
          <ReactMarkdown
            components={{
              p: ({node, ...props}) => (
                <p style={{ 
                  margin: '0.25rem 0',
                  color: isUser ? '#1B5E20' : '#0A4B8C',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left'
                }} {...props} />
              ),
              a: CustomLink,
              code: ({node, ...props}) => (
                <code style={{ 
                  backgroundColor: isUser ? 'rgba(27, 94, 32, 0.1)' : 'rgba(10, 75, 140, 0.1)',
                  padding: '0.2rem 0.4rem',
                  borderRadius: '4px',
                  color: isUser ? '#1B5E20' : '#0A4B8C',
                  direction: isRTL ? 'rtl' : 'ltr'
                }} {...props} />
              )
            }}
          >
            {message.content}
          </ReactMarkdown>
        </Message.Content>
      </Message>
    </div>
  );
};

const Chat = ({ is_user_signed_in, allow_chat, current_locale, experiment_id, initialMessages = [], skipIntro = false }) => {
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { height } = useWindowSize();
  const tempExperimentIdRef = useRef(experiment_id || `temp-${Date.now()}`);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [messages, setMessages] = useState(() => {
    // Check if we have stored messages in window object
    if (window.chatHistory && window.chatHistory[experiment_id]) {
      return window.chatHistory[experiment_id];
    }
    return initialMessages;
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortController = useRef(null);
  const isRTL = current_locale === 'ar';

  // Share temp_experiment_id with parent components
  useEffect(() => {
    if (!experiment_id) {
      window.temp_experiment_id = tempExperimentIdRef.current;
    }
    return () => {
      // Clean up temp ID when component unmounts
      if (window.temp_experiment_id === tempExperimentIdRef.current) {
        delete window.temp_experiment_id;
      }
    };
  }, [experiment_id]);

  // Clear error message when user signs in
  useEffect(() => {
    if (is_user_signed_in) {
      setError(null);
      handleSubmit(null);
    }
  }, [is_user_signed_in]);

  const translations = {
    en: {
      signInMessage: 'Please <a href="/users/sign_in/" target="_blank" rel="noopener noreferrer">sign in</a> or <a href="/users/sign_up" target="_blank" rel="noopener noreferrer">sign up</a> to send messages',
      placeholder: 'Type your message...',
      chatNotAllowed: 'Chatting not allowed',
      rateLimitMessage: 'Message limit reached. You can send another message in',
      seconds: 'seconds',
      deleteButton: 'Delete All Messages',
      deleteConfirm: 'Are you sure you want to delete all chat messages?',
      cancel: 'Cancel',
      delete: 'Delete'
    },
    ar: {
      signInMessage: '<a href="/users/sign_in/" target="_blank" rel="noopener noreferrer">قم بتسجيل الدخول</a> أو <a href="/users/sign_up" target="_blank" rel="noopener noreferrer">إنشاء حساب</a> لإرسال الرسائل',
      placeholder: 'اكتب رسالتك...',
      chatNotAllowed: 'الدردشة غير مسموح بها',
      rateLimitMessage: 'تم الوصول إلى حد الرسائل. يمكنك إرسال رسالة أخرى في',
      seconds: 'ثواني',
      deleteButton: 'حذف جميع الرسائل',
      deleteConfirm: 'هل أنت متأكد أنك تريد حذف جميع رسائل الدردشة؟',
      cancel: 'إلغاء',
      delete: 'حذف'
    },
    tr: {
      signInMessage: 'Mesaj göndermek için lütfen <a href="/users/sign_in/" target="_blank" rel="noopener noreferrer">giriş yapın</a> veya <a href="/users/sign_up" target="_blank" rel="noopener noreferrer">kaydolun</a>',
      placeholder: 'Mesajınızı yazın...',
      chatNotAllowed: 'Sohbet izni yok',
      rateLimitMessage: 'Mesaj sınırına ulaşıldı. Başka bir mesaj gönderebilmeniz için kalan süre',
      seconds: 'saniye',
      deleteButton: 'Tüm Mesajları Sil',
      deleteConfirm: 'Tüm sohbet mesajlarını silmek istediğinizden emin misiniz?',
      cancel: 'İptal',
      delete: 'Sil'
    },
    de: {
      signInMessage: 'Bitte <a href="/users/sign_in/" target="_blank" rel="noopener noreferrer">melden Sie sich an</a> oder <a href="/users/sign_up" target="_blank" rel="noopener noreferrer">registrieren Sie sich</a>, um Nachrichten zu senden',
      placeholder: 'Schreiben Sie Ihre Nachricht...',
      chatNotAllowed: 'Chatten nicht erlaubt',
      rateLimitMessage: 'Nachrichtenlimit erreicht. Sie können in',
      seconds: 'Sekunden eine weitere Nachricht senden',
      deleteButton: 'Alle Nachrichten löschen',
      deleteConfirm: 'Sind Sie sicher, dass Sie alle Chat-Nachrichten löschen möchten?',
      cancel: 'Abbrechen',
      delete: 'Löschen'
    },
    es: {
      signInMessage: 'Por favor <a href="/users/sign_in/" target="_blank" rel="noopener noreferrer">inicie sesión</a> o <a href="/users/sign_up" target="_blank" rel="noopener noreferrer">regístrese</a> para enviar mensajes',
      placeholder: 'Escriba su mensaje...',
      chatNotAllowed: 'Chat no permitido',
      rateLimitMessage: 'Límite de mensajes alcanzado. Puede enviar otro mensaje en',
      seconds: 'segundos',
      deleteButton: 'Borrar todos los mensajes',
      deleteConfirm: '¿Está seguro de que desea eliminar todos los mensajes del chat?',
      cancel: 'Cancelar',
      delete: 'Borrar'
    },
    fr: {
      signInMessage: 'Veuillez vous <a href="/users/sign_in/" target="_blank" rel="noopener noreferrer">connecter</a> ou vous <a href="/users/sign_up" target="_blank" rel="noopener noreferrer">inscrire</a> pour envoyer des messages',
      placeholder: 'Tapez votre message...',
      chatNotAllowed: 'Chat non autorisé',
      rateLimitMessage: 'Limite de messages atteinte. Vous pourrez envoyer un autre message dans',
      seconds: 'secondes',
      deleteButton: 'Supprimer tous les messages',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer tous les messages du chat ?',
      cancel: 'Annuler',
      delete: 'Supprimer'
    }
  };

  const currentTranslations = translations[current_locale] || translations.en;

  useEffect(() => {
    let timer;
    if (rateLimitCountdown > 0) {
      timer = setInterval(() => {
        setRateLimitCountdown(prev => {
          if (prev <= 1) {
            setError(null); // Clear error when countdown finishes
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [rateLimitCountdown]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ui.message.no-margin-message {
        margin-top: 0 !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Save messages to window storage whenever they change
  useEffect(() => {
    if (!window.chatHistory) {
      window.chatHistory = {};
    }
    window.chatHistory[experiment_id] = messages;
    // Notify AISideTabs of message updates
    window.dispatchEvent(new CustomEvent('chatHistoryUpdated', {
      detail: { experimentId: experiment_id }
    }));
  }, [messages, experiment_id]);

  // Save messages before unmounting
  useEffect(() => {
    return () => {
      if (!window.chatHistory) {
        window.chatHistory = {};
      }
      window.chatHistory[experiment_id] = messages;
    };
  }, [messages, experiment_id]);

  React.useEffect(() => {
    // Initialize window.chatHistory if it doesn't exist
    if (!window.chatHistory) {
      window.chatHistory = {};
    }

    if (!skipIntro && messages.length === 0) {
      const introMessage = {
        role: 'assistant',
        content: `Welcome to DCACLab AI Assistant! 👋

I'm here to help you with:
• Digital circuit design and analysis
• Circuit troubleshooting and optimization
• Lab safety and best practices
• Technical concepts and explanations

Feel free to ask any questions about your circuit or digital electronics in general!`,
        created_at: new Date().toISOString()
      };
      const newMessages = [introMessage];
      setMessages(newMessages);
      window.chatHistory[experiment_id] = newMessages;
    } else if (initialMessages && initialMessages.length > 0 && !window.chatHistory[experiment_id]) {
      window.chatHistory[experiment_id] = initialMessages;
      setMessages(initialMessages);
    }
  }, [initialMessages, skipIntro, experiment_id]);

  useEffect(() => {
    // Expose delete functionality globally for AISideTabs
    window.handleDeleteMessages = () => !isDeleting && setShowDeleteModal(true);
    
    return () => {
      // Cleanup when component unmounts
      delete window.handleDeleteMessages;
    };
  }, [isDeleting]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (rateLimitCountdown > 0) {
      return;
    }

    let messageContent;
    let updatedMessages;
    
    if (e === null && messages.length > 0) {
      // If e is null, check if last message was from user (unprocessed)
      if (messages[messages.length - 1].role !== 'user') return;
      // Use existing messages without adding a new one
      updatedMessages = messages;
      messageContent = messages[messages.length - 1].content;
    } else {
      if (!input.trim() || isLoading) return;
      messageContent = input.trim();
      setInput('');
      
      const userMessage = { 
        role: 'user', 
        content: messageContent,
        created_at: new Date().toISOString()
      };
      
      // Remove intro message if it exists and this is the first user message
      updatedMessages = messages.length === 1 && messages[0].role === 'assistant' ? 
        [userMessage] : 
        [...messages, userMessage];
      setMessages(updatedMessages);
    }
    
    // Update window storage
    window.chatHistory[experiment_id] = updatedMessages;
    setIsLoading(true);

    // Create a new AbortController for this request
    abortController.current = new AbortController();

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ 
          messages: updatedMessages,
          experiment_id: experiment_id,
          temp_experiment_id: !experiment_id ? tempExperimentIdRef.current : undefined
        }),
        signal: abortController.current.signal,
        credentials: 'same-origin'
      });

      if (!response.ok) {
        if (response.status === 401) {
          if (!is_user_signed_in) {
            setError(currentTranslations.signInMessage);
          }
        } else if (response.status === 429) {
          const data = await response.json();
          setRateLimitCountdown(data.seconds_remaining || 60); // Use actual seconds remaining
          setError(`${currentTranslations.rateLimitMessage} ${data.seconds_remaining || 60} ${currentTranslations.seconds}`);
          // Remove the last user message since it wasn't processed
          setMessages(prev => prev.slice(0, -1));
          return;
        } else {
          throw new Error('Network response was not ok');
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = { 
        role: 'assistant', 
        content: '',
        created_at: new Date().toISOString()
      };
      
      // Add initial assistant message
      setMessages(prev => [...prev, assistantMessage]);
      setIsStreaming(true);
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(5);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices[0]?.delta?.content || '';
              
              // Add a small delay between updates
              await new Promise(resolve => setTimeout(resolve, 10));
              
              // Update messages immutably
              setMessages(prev => {
                const newMessages = prev.map((msg, idx) => {
                  if (idx === prev.length - 1) {
                    return {
                      ...msg,
                      content: msg.content + content
                    };
                  }
                  return msg;
                });
                return newMessages;
              });
            } catch (e) {
              console.error('Error parsing chunk:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Request was aborted');
      } else {
        console.error('Error:', err);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, there was an error processing your request.' 
        }]);
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      abortController.current = null;
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleStop = () => {
    if (abortController.current) {
      abortController.current.abort();
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    if (messages.length >= 2) {
      const lastUserMessage = messages[messages.length - 2];
      setMessages(prev => prev.slice(0, -1));
      handleSubmit({ preventDefault: () => {} });
    }
  };

  const handleDeleteMessages = async () => {
    try {
      setIsDeleting(true);
      // Add temp_experiment_id to the query parameters if experiment_id is not present
      const queryParams = experiment_id ? 
        `experiment_id=${experiment_id}` : 
        `temp_experiment_id=${tempExperimentIdRef.current}`;
        
      const response = await fetch(`/api/chat/delete_messages?${queryParams}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      });
      
      if (response.ok) {
        // Create new intro message
        const introMessage = {
          role: 'assistant',
          content: `Welcome to DCACLab AI Assistant! 👋

I'm here to help you with:
• Digital circuit design and analysis
• Circuit troubleshooting and optimization
• Lab safety and best practices
• Technical concepts and explanations

Feel free to ask any questions about your circuit or digital electronics in general!`,
          created_at: new Date().toISOString()
        };
        
        // Set messages with just the intro message
        setMessages([introMessage]);
        
        // Update window storage and dispatch event
        if (window.chatHistory) {
          window.chatHistory[experiment_id || tempExperimentIdRef.current] = [introMessage];
          // Dispatch custom event to notify AISideTabs
          window.dispatchEvent(new CustomEvent('chatHistoryUpdated', {
            detail: { experimentId: experiment_id || tempExperimentIdRef.current }
          }));
        }
        
        setShowDeleteModal(false);
      } else {
        console.error('Failed to delete messages');
      }
    } catch (error) {
      console.error('Error deleting messages:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Add this new useEffect for preloading
  useEffect(() => {
    preloadImage(LOGO);
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    }}>
      <div 
        style={{
          flex: '1 1 0',
          overflowY: 'auto',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          minHeight: 0
        }}
      >
        {messages.map((message, index) => (
          <ChatMessage
            key={index}
            message={message}
            isLoading={isLoading && index === messages.length - 1}
            current_locale={current_locale}
            messages={messages}
          />
        ))}
        {isLoading && !isStreaming && (
          <ThinkingMessage current_locale={current_locale} />
        )}
        {(error || rateLimitCountdown > 0) && (
          <Message 
            warning
            icon
            style={{ 
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left',
              marginBottom: '1rem',
              minHeight: '4rem',
              display: 'flex',
              alignItems: 'center',
              padding: '0.5rem'
            }}
          >
            <Icon 
              name='clock outline' 
              loading={rateLimitCountdown > 0} 
              style={{
                margin: isRTL ? '0 0 0 1rem' : '0 1rem 0 0.5rem',
                opacity: rateLimitCountdown > 0 ? 1 : 0.8
              }}
            />
            <Message.Content style={{ flex: 1 }}>
              {rateLimitCountdown > 0 ? (
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.25rem 0'
                }}>
                  <span>{currentTranslations.rateLimitMessage}</span>
                  <span style={{ 
                    fontWeight: 'bold',
                    minWidth: '2rem',
                    textAlign: 'center'
                  }}>
                    {rateLimitCountdown}
                  </span>
                  <span>{currentTranslations.seconds}</span>
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: error }} />
              )}
            </Message.Content>
          </Message>
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        ref={inputRef}
        input={input}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        allow_chat={allow_chat}
        rateLimitCountdown={rateLimitCountdown}
        placeholder={currentTranslations.placeholder}
        chatNotAllowed={currentTranslations.chatNotAllowed}
        isRTL={isRTL}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '90%',
            width: '400px'
          }}>
            <h3>{currentTranslations.deleteConfirm}</h3>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
              marginTop: '1.5rem'
            }}>
              <Button 
                onClick={() => setShowDeleteModal(false)} 
                disabled={isDeleting}
              >
                {currentTranslations.cancel}
              </Button>
              <Button 
                negative 
                onClick={handleDeleteMessages}
                loading={isDeleting}
                disabled={isDeleting}
              >
                {currentTranslations.delete}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
